import React, { useEffect, useState } from "react";
import "./ProductControl.scss";
import { Button, Checkbox, Col, Flex, Input, Row, Select, Tooltip } from "antd";
import Moisture from "@assets/icons/Moisture.svg";
import Rain from "@assets/icons/Rain.svg";
import HayCut from "@assets/icons/Hay-cut.svg";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import distanceIcon from "@assets/icons/distanceIcon.png";
import Helpers from "@utils/Helpers";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useUnit } from "contexts/UnitContext";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useCart } from "contexts/CartContext";
import { useWishlist } from "contexts/WishlistContext";
import Validates from "utils/Validates";
import { useUser } from "contexts/UserContext";

const ProductControl: React.FC<any> = ({
  sellingMethod,
  data,
  distance,
  deliveryTime,
  expanded,
}) => {
  const { units, convertDistance, convertCurrency } = useUnit();
  const [autoBidding, setAutoBidding] = useState(false);
  const [buyQuantity, setBuyQuantity] = useState(data?.quantity || 0);
  const [shippingFee, setShippingFee] = useState<number | undefined>(undefined);
  const { addToCart } = useCart();
  const {
    add: addToWishlist,
    remove: removeFromWishlist,
    isInWishlist,
    isLoading: isWishlistLoading,
  } = useWishlist();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const { userProfile } = useUser();
  const currentDistance = convertDistance(distance / 1000);
  const productId = data?.product_id;

  useEffect(() => {
    if (data?.quantity) {
      setBuyQuantity(data.quantity);
    }
  }, [data?.quantity]);

  const handleAddToCart = async () => {
    if (!data?.product_id) return;
    if (buyQuantity <= 0) {
      Helpers.notification.warning("Please enter quantity");
      return;
    }
    setIsAdding(true);
    const payload = {
      product_id: data.product_id,
      quantity: buyQuantity,
    };
    await addToCart(payload);
    setIsAdding(false);
  };

  const handleAddToWishlist = async () => {
    if (!productId) return;
    if (!isAuthenticated) {
      navigate("/auth");
      return;
    }
    if (isInWishlist(productId)) {
      await removeFromWishlist(productId);
      return;
    }
    await addToWishlist(productId);
  };

  const handleBuyItNow = async () => {
    if (!data?.product_id) return;
    if (buyQuantity <= 0) {
      Helpers.notification.warning("Please enter quantity");
      return;
    }
    setIsAdding(true);
    const payload = {
      product_id: data.product_id,
      quantity: buyQuantity,
    };
    await addToCart(payload);
    setIsAdding(false);
    navigate("/checkout?productId=" + data.product_id);
  };

  const handleQuantityChange = (e: any) => {
    const inputValue = Number(Validates.validateNumberForm(e.target.value));
    if (inputValue > (data?.quantity ?? 0)) return;
    setBuyQuantity(inputValue);
  };

  return (
    <div className="ProductControl">
      {/* {sellingMethod !== "fixed price" && (
        <Flex justify="center" align="center">
          <div className="bid-duration">
            <div className="bid-time">
              {time.days} days {formatTime(time.hours)}:
              {formatTime(time.minutes)}:{formatTime(time.seconds)}
            </div>
            <div className="bid-start-date">Sat, 11:30AM</div>
          </div>
          <div className="bid-amount ms-auto">2 bids</div>
        </Flex>
      )} */}

      <Row className="mt-4">
        <Col span={expanded ? 0 : 9}>
          <div className="product-info">
            <span>
              <img src={distanceIcon} width={15} alt="dist" /> Distance:{" "}
            </span>
            {Helpers.formatNumber(currentDistance, 0)} {units.distance}
          </div>
          <div className="product-info">
            <span>
              <FontAwesomeIcon icon={faClock} /> Time:{" "}
            </span>
            {Helpers.convertSecondsToHoursAndMinutes(deliveryTime)}
          </div>
          <div className="product-info"></div>
          {sellingMethod === "auction" && (
            <>
              <div className="product-info">
                <span>Starting Bid: </span>$90/bale
              </div>
              <div className="product-info">
                <span>Latest Bid: </span>$100/bale
              </div>
            </>
          )}
          <div className="product-info">
            <span>Price / ton: </span>$
            {Helpers.formatNumber(
              convertCurrency(data?.price_per_tons, "USD", units.currency),
              2
            )}
          </div>
          <div className="product-info">
            <span>Price / bale: </span>$
            {Helpers.formatNumber(
              convertCurrency(data?.price_per_bale, "USD", units.currency),
              2
            )}
          </div>
          <div className="product-info">
            <span>Weight / bale: </span>
            {Helpers.formatNumber(data?.bale_weight / 1000, 2)} ton
          </div>
          <div className="product-info">
            <span>Quantity: </span>
            {Helpers.formatNumber(data?.quantity, 0)} bales
          </div>
          <div className="product-info">
            <img className="me-2" src={HayCut} alt="haycut" width={16} />
            <span>Hay Cut: </span>
            {data?.hay_cut}
          </div>
          <div className="product-info">
            <img className="me-2" src={Moisture} alt="moist" />
            <span>Moisture: </span>
            {data?.moisture}%
          </div>
          <div className="product-info">
            <img className="me-2" src={Rain} alt="rain" />
            <span>Rain: </span>
            {data?.rain ? "Yes" : "No"}
          </div>
          {sellingMethod === "fixed price" && (
            <div className="product-quantity mt-3 pe-4">
              <Input
                className="w-100"
                suffix={"Bale"}
                defaultValue={data?.quantity}
                min={0}
                max={data?.quantity}
                value={buyQuantity}
                onChange={(e: any) => handleQuantityChange(e)}
              />
            </div>
          )}
        </Col>
        <Col span={expanded ? 24 : 15}>
          <div className="postal-and-shipping-wrap">
            <Flex gap={12} align="center">
              <Input
                size="large"
                value={data?.farm_details?.farm_postal_code}
                disabled
              />
              <Select
                size="large"
                value={shippingFee}
                placeholder="Shipping Options"
                onChange={(value) => setShippingFee(value)}
              >
                {data !== null &&
                  Object.entries(data?.shipping_option_details).map(
                    ([key, value]: any, index: number) => {
                      if (key !== "carrier_shipping") {
                        return (
                          <Select.Option
                            key={index}
                            value={value === true ? 0 : value}
                          >
                            {key == "consumer_pickup"
                              ? "Consumer Pickup"
                              : "Shipping by Producer"}
                          </Select.Option>
                        );
                      } else {
                        return (value as any)?.map((ite: any, idx: number) => (
                          <Select.Option key={index + idx} value={ite.cost}>
                            {ite.unit}
                          </Select.Option>
                        ));
                      }
                    }
                  )}
              </Select>
            </Flex>
          </div>
          <div className="bidding-recipe">
            <div className="total-price">
              Estimated:
              {!isAuthenticated ? (
                <Button
                  className="login-btn"
                  onClick={() => {
                    navigate("/auth");
                  }}
                >
                  Login to view estimated price
                </Button>
              ) : (
                <>
                  <span className="estimate-price">
                    $
                    {buyQuantity < 1
                      ? "0.00"
                      : Helpers.formatNumber(
                          convertCurrency(
                            data?.price_per_bale,
                            "USD",
                            units.currency
                          ) *
                            buyQuantity +
                            (shippingFee || 0) * currentDistance,
                          2
                        )}
                  </span>
                  total
                </>
              )}
            </div>
            <Flex align="center" justify="space-between">
              <div className="value-operator">
                <div className="value">
                  $
                  {Helpers.formatNumber(
                    convertCurrency(
                      data?.price_per_bale,
                      "USD",
                      units.currency
                    ),
                    2
                  )}
                </div>
                <div className="unit">/ bale</div>
              </div>
              <div className="operator">x</div>
              <div className="value-operator">
                <div className="value">{buyQuantity}</div>
                <div className="unit">Bales</div>
              </div>
              <div className="operator">+</div>
              <div className="value-operator">
                <div className="value">
                  $
                  {Helpers.formatNumber(
                    ((shippingFee || 0) * distance) / 1000,
                    2
                  )}
                </div>
                <div className="unit">
                  Shipping Cost{" "}
                  <Tooltip
                    placement="bottom"
                    title={
                      <span>
                        Shipping cost is calculated by: <br />
                        $4.35/km * Distance: {currentDistance.toFixed(1)}{" "}
                        {units.distance}
                      </span>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
              </div>
            </Flex>
          </div>

          {sellingMethod !== "fixed price" && (
            <div className="bidding-control">
              <Input className="mb-3" size="large" placeholder="Your Offer*" />
              <Row
                gutter={14}
                className={`mb-1 auto-bidding-form ${autoBidding && "display"}`}
              >
                <Col span={9}>
                  <Input size="large" placeholder="Increment*" />
                </Col>
                <Col span={15}>
                  <Input size="large" placeholder="Your Max Offer*" />
                </Col>
              </Row>
              <Checkbox
                checked={autoBidding}
                onChange={() => setAutoBidding(!autoBidding)}
              >
                Automatic Bidding
              </Checkbox>
            </div>
          )}

          <div className="bidding-action">
            <Button
              className="bid-btn"
              onClick={handleBuyItNow}
              disabled={
                data?.farm_details?.producer_id === userProfile?.user_id
              }
            >
              {sellingMethod === "fixed price" ? "Buy It Now" : "Place a Bid"}
            </Button>
            <Button
              className="add-btn"
              loading={isWishlistLoading}
              onClick={handleAddToWishlist}
              disabled={
                data?.farm_details?.producer_id === userProfile?.user_id
              }
            >
              {productId && isInWishlist(productId)
                ? "Remove from Watchlist"
                : "Add to Watchlist"}
            </Button>
            {sellingMethod === "fixed price" && (
              <Button
                className="add-cart-btn"
                loading={isAdding}
                onClick={handleAddToCart}
                disabled={
                  data?.farm_details?.producer_id === userProfile?.user_id
                }
              >
                Add to Cart
              </Button>
            )}
          </div>
        </Col>
      </Row>
      <div></div>
    </div>
  );
};

export default ProductControl;
